import { useEffect, useMemo, useState } from 'react';
import { projectFirestore } from '../firebase/config';

const ONLINE_WINDOW_MS = 2 * 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;

function toMillis(value) {
  if (!value) return 0;
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (typeof value.seconds === 'number') return value.seconds * 1000;
  if (typeof value === 'string' || value instanceof Date) {
    const parsed = new Date(value).getTime();
    return Number.isNaN(parsed) ? 0 : parsed;
  }
  return 0;
}

function isSameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

export default function useVisitorStats(periodDays = null) {
  const [visitors, setVisitors] = useState([]);
  const [adminUsersCount, setAdminUsersCount] = useState(0);
  const [loadingVisitors, setLoadingVisitors] = useState(true);
  const [loadingAdmins, setLoadingAdmins] = useState(true);

  useEffect(() => {
    const unsubVisitors = projectFirestore.collection('siteVisitors').onSnapshot((snap) => {
      const list = [];
      snap.forEach((doc) => {
        list.push({ id: doc.id, ...doc.data() });
      });
      setVisitors(list);
      setLoadingVisitors(false);
    }, () => {
      setVisitors([]);
      setLoadingVisitors(false);
    });

    const unsubAdmins = projectFirestore.collection('adminUsers').onSnapshot((snap) => {
      setAdminUsersCount(snap.size);
      setLoadingAdmins(false);
    }, () => {
      setAdminUsersCount(0);
      setLoadingAdmins(false);
    });

    return () => {
      unsubVisitors();
      unsubAdmins();
    };
  }, []);

  const stats = useMemo(() => {
    const now = Date.now();
    const today = new Date();
    const periodStart = typeof periodDays === 'number' && periodDays > 0
      ? now - (periodDays * DAY_MS)
      : null;

    const decorated = visitors.map((v) => {
      const lastSeenMs = toMillis(v.lastSeen);
      const firstSeenMs = toMillis(v.firstSeen);
      const activeByTime = lastSeenMs > 0 && (now - lastSeenMs) <= ONLINE_WINDOW_MS;
      return {
        ...v,
        lastSeenMs,
        firstSeenMs,
        activeByTime,
      };
    });

    const inPeriod = (v) => {
      if (periodStart === null) return true;
      const base = v.lastSeenMs || v.firstSeenMs;
      return base >= periodStart;
    };

    const filtered = decorated.filter(inPeriod);

    const onlineVisitors = filtered.filter((v) => v.isOnline && v.activeByTime);
    const onlineAdminSessions = onlineVisitors.filter((v) => v.isAdminSession);

    const todayVisitors = filtered.filter((v) => {
      if (!v.firstSeenMs) return false;
      return isSameDay(new Date(v.firstSeenMs), today);
    });

    const activeLast24h = filtered.filter((v) => v.lastSeenMs > 0 && (now - v.lastSeenMs) <= DAY_MS);

    const recentVisitors = [...filtered]
      .sort((a, b) => b.lastSeenMs - a.lastSeenMs)
      .slice(0, 8);

    return {
      totalVisitors: filtered.length,
      onlineVisitors: onlineVisitors.length,
      todayVisitors: todayVisitors.length,
      activeLast24h: activeLast24h.length,
      onlineAdminSessions: onlineAdminSessions.length,
      recentVisitors,
      lastUpdatedAt: now,
    };
  }, [visitors, periodDays]);

  return {
    ...stats,
    adminUsersCount,
    loading: loadingVisitors || loadingAdmins,
  };
}
